import { ScramjetContext } from "@/shared";
import { rewriteUrl, URLMeta } from "@rewriters/url";
import { JSON_parse, JSON_stringify, Object_keys, _URL } from "@/shared/snapshot";

// An import map (<script type="importmap">) maps module addresses to other addresses: bare
// names ("react" → a file), whole files, and folders by prefix ("…/v2.12/static/js/" →
// "…/static/js/", CrazyGames). The browser applies it to the addresses modules are actually
// loaded from, which here are proxied ones, so every address in the map, keys included, has
// to be in proxied form too. Folder prefixes get the bare proxied form (no Scramjet query):
// the rest of the requested address, Scramjet's query included, is kept after them.

const isAddress = (text: string) =>
	text.startsWith("/") ||
	text.startsWith("./") ||
	text.startsWith("../") ||
	/^[a-z][a-z0-9+.-]*:/i.test(text);

/** The proxied form of an address, without Scramjet's query; null if not http(s). */
function proxiedPrefix(
	address: string,
	context: ScramjetContext,
	meta: URLMeta
): string | null {
	let real: _URL;
	try {
		real = new _URL(address, meta.base.href);
	} catch {
		return null;
	}
	if (real.protocol !== "http:" && real.protocol !== "https:") return null;

	return context.prefix.href + context.interface.codecEncode(real.href);
}

function rewriteSpecifierMap(
	map: Record<string, unknown>,
	context: ScramjetContext,
	meta: URLMeta
): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const key of Object_keys(map)) {
		const value = map[key];
		const isPrefix = key.endsWith("/");
		const target =
			typeof value !== "string"
				? value
				: isPrefix
					? (proxiedPrefix(value, context, meta) ?? value)
					: rewriteUrl(value, context, meta, { isModule: true });
		if (!isAddress(key)) {
			out[key] = target; // a bare name, as the page imports it
			continue;
		}
		if (isPrefix) {
			out[proxiedPrefix(key, context, meta) ?? key] = target;
		} else {
			// A whole file: as dynamic import() asks for it (with Scramjet's query) and bare.
			out[rewriteUrl(key, context, meta, { isModule: true })] = target;
			out[proxiedPrefix(key, context, meta) ?? key] = target;
		}
	}

	return out;
}

/** An import map's JSON with every address in proxied form; the input if it is not JSON. */
export function rewriteImportMap(
	json: string,
	context: ScramjetContext,
	meta: URLMeta
): string {
	let map: any;
	try {
		map = JSON_parse(json);
	} catch {
		return json; // invalid: the browser ignores it either way
	}
	if (!map || typeof map !== "object") return json;
	if (map.imports && typeof map.imports === "object") {
		map.imports = rewriteSpecifierMap(map.imports, context, meta);
	}
	if (map.scopes && typeof map.scopes === "object") {
		const scopes: Record<string, unknown> = {};
		for (const scope of Object_keys(map.scopes)) {
			const inner = map.scopes[scope];
			scopes[proxiedPrefix(scope, context, meta) ?? scope] =
				inner && typeof inner === "object"
					? rewriteSpecifierMap(inner, context, meta)
					: inner;
		}
		map.scopes = scopes;
	}

	return JSON_stringify(map);
}
